import { inject, Injectable } from '@angular/core';
import { USER } from '../constants/storage';
import {
    PermissionDto,
    PermissionValuesDto,
} from 'src/app/shared/dto/permission.dto';
import { UserDto } from 'src/app/shared/dto/user.dto';
import { LocalStorageService } from './storage.service';
import { environment } from 'src/environments/environment';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject,lastValueFrom, Subscription } from 'rxjs';
import { BaseService } from 'src/app/shared/services/base.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { Client, formatClientRoom, RoomNames } from 'src/app/shared/sockets/enums/rooms';

@Injectable({
    providedIn: 'root',
})
export class PermissionService extends BaseService{

    private _permissions: PermissionDto[] = [];
    private permissionsSocketSubscription: Subscription | undefined;
    private readonly _permissionsChanged$ = new BehaviorSubject<void>(undefined);
    public readonly permissionsChanged$ = this._permissionsChanged$.asObservable();

    private readonly localStorageService = inject(LocalStorageService);
    private readonly socketService = inject(SocketService);

    constructor(httpClient: HttpClient) {
        super(httpClient, 'permissions');
        this.loadPermissions();
        this.setupPermissionsSocket();
    }

    
    public getPermissionByKeys(mainKey: string, permissionKey: string): PermissionValuesDto | undefined {
        try {
            if (!this._permissions || this._permissions.length === 0) {
                this.loadPermissions();
            }

            if (this._permissions.length === 0) {
                return undefined;
            }

            const searchMain = mainKey.toLowerCase();
            const searchKey = permissionKey.toLowerCase();

            // 1. Exact match: mainkey === mainKey AND key === permissionKey
            let permission = this._permissions.find(p =>
                (p.mainkey ?? '').toLowerCase() === searchMain &&
                (p.key ?? '').toLowerCase() === searchKey
            );

            // 2. Fallback: if not found and permissionKey is not empty,
            //    try treating permissionKey as a mainkey with empty key.
            //    E.g., canRead('productions', 'productionProcesses') → find mainkey='productionProcesses', key=''
            if (!permission && searchKey) {
                permission = this._permissions.find(p =>
                    (p.mainkey ?? '').toLowerCase() === searchKey &&
                    (p.key ?? '').toLowerCase() === ''
                );
            }

            return permission?.values;
        } catch (e: any) {
            throw new Error(`No se ha podido obtener el permiso ${permissionKey} de ${mainKey}: Error: ${e.message}`);
        }
    }
    //MBV Se comenta este método porque no se está usando y unicamente lo tiene la tabla de permisosApp no está para permisos por usuario o perfil
    // public hasPermission(permissionKeys: string[]): boolean {  
    //     if (this._permissions.length === 0) {
    //         this.loadPermissions();
    //     }

    //     if (this._permissions) {
    //         permissionKeys.forEach((key): void => {
    //             const permission = this._permissions.find((permission) => permission.key === key);

    //             if (!permission || !permission.activated) {
    //                 return;
    //             }
    //         });

    //         return true;
    //     }

    //     return false;
    // }

    public canRead(mainKey: string, permissionKey: string): boolean {
        try {
            if (this._permissions.length === 0 || !mainKey) {
                return false;
            }
            let response = this.getPermissionByKeys(mainKey, permissionKey);
            if (!response) {
                console.log(`No se ha encontrado el permiso de lectura de ${permissionKey} en ${mainKey}`);
            }
            return (response?.read ?? false);
        } catch (e: any) {
            throw new Error(`No se ha podido obtener el permiso de lectura de ${permissionKey} en ${mainKey}: Error: ${e.message}`);
        }
    }

    public canCreate(mainKey: string, permissionKey: string): boolean {
        try {
            if (this._permissions.length === 0 || !mainKey) {
                return false;
            }
            let response = this.getPermissionByKeys(mainKey, permissionKey);
            if (!response) {
                console.log(`No se ha encontrado el permiso de creación de ${permissionKey} en ${mainKey}`);
            }
            return (response?.create ?? false);
        } catch (e: any) {
            throw new Error(`No se ha podido obtener el permiso de creación de ${permissionKey} en ${mainKey}: Error: ${e.message}`);
        }
    }

    public canEdit(mainKey: string, permissionKey: string): boolean {
        try {
            if (this._permissions.length === 0 || !mainKey) {
                return false;
            }
            let response = this.getPermissionByKeys(mainKey, permissionKey);
            if (!response) {
                console.log(`No se ha encontrado el permiso de edición de ${permissionKey} en ${mainKey}`);
            }
            return (response?.edit ?? false);
        } catch (e: any) {
            throw new Error(`No se ha podido obtener el permiso de edición de ${permissionKey} en ${mainKey}: Error: ${e.message}`);
        }
    }

    public canDelete(mainKey: string, permissionKey: string): boolean {
        try {
            if (this._permissions.length === 0 || !mainKey) {
                return false;
            }
            let response = this.getPermissionByKeys(mainKey, permissionKey);
            if (!response) {
                console.log(`No se ha encontrado el permiso de edición de ${permissionKey} en ${mainKey}`);
            }
            return (response?.delete ?? false);
        } catch (e: any) {
            throw new Error(`No se ha podido obtener el permiso de borrado de ${permissionKey} en ${mainKey}: Error: ${e.message}`);
        }
    }

    private normalizePermissions(rawPerms: any[]): PermissionDto[] {
    return rawPerms.map(p => ({
        id: p.permissionId ?? p.id,
        mainkey: p.mainkey,
        key: p.key ?? '',
        values: {
            read:   p.canRead   ?? p.values?.read   ?? false,
            create: p.canCreate ?? p.values?.create ?? false,
            edit:   p.canWrite  ?? p.values?.edit   ?? false,
            delete: p.canDelete ?? p.values?.delete ?? false,
        }
    }));
}

    public loadPermissions(): void {
        const userStr = this.localStorageService.getItem(USER);

        if (userStr) {
            const user = userStr as UserDto;

            if (user) {
                const rawPerms = user.permissions ?? [];
            this._permissions = this.normalizePermissions(rawPerms);
            } else {
                throw new Error('User profile not found');
            }

            return;
        }

        this._permissions = [];
    }

    private setupPermissionsSocket(): void {
        this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.PERMISSIONS_ROOM));
        this.permissionsSocketSubscription = this.socketService.getNotifications(Messages.PERMISSIONS_CHANGED).subscribe(async () => {
            await this.reloadUserPermissions();
        });
    }
public async reloadPermissionsNow(): Promise<void> {
    console.log('🔵 reloadPermissionsNow ejecutado');  // 👈 añade este log
    await this.reloadUserPermissions();
}

  private async reloadUserPermissions(): Promise<void> {
    // 👇 Declarar FUERA del try para que TypeScript vea el scope completo
    const userStr = this.localStorageService.getItem(USER);
    if (!userStr) return;

    const currentUser = userStr as UserDto;
    if (!currentUser?.id) return;

    try {
        console.log('🔄 Recargando permisos para userId:', currentUser.id);

        const usersArray: any[] = await lastValueFrom(
            this.httpClient.get<any[]>(`${environment.apiUrl}users/${currentUser.id}`)
        );

        const updatedUser = Array.isArray(usersArray)
            ? usersArray.find((u: any) => u.id === currentUser.id)
            : usersArray;

        console.log('📦 updatedUser encontrado:', updatedUser);

            if (updatedUser) {
            this.localStorageService.setItem(USER, updatedUser);
 
            const rawPerms = (updatedUser as any).permissions ?? [];
            console.log('🔍 Primer permiso raw:', JSON.stringify(rawPerms[0]));

            this._permissions = this.normalizePermissions(rawPerms);
           console.log('🔍 Primer permiso normalizado:', JSON.stringify(this._permissions[0]));

            this._permissionsChanged$.next();
        }
    } catch (e: any) {
        console.error('❌ Error al recargar permisos:', e.message);
    }
}


    async findAllPermisionsApp(): Promise<PermissionDto[]> {
        try {
            const request = this.httpClient.get<PermissionDto>(`${this.url}/findAll`);

            const response: PermissionDto = await lastValueFrom(request);

            if (!response) {
                throw null;
            }

            return Array.isArray(response) ? response : [response];
        } catch (e: any) {
            throw new Error(`No se ha encontrado ningun permiso. Error: ${e.message}`);
        }
    }

    async findAllPermisionsProfile(profileId: number): Promise<PermissionDto[]> {
        try {
            const request = this.httpClient.get<PermissionDto>(`${this.url}/findAllProfile?profileId=${profileId}`);

            const response: PermissionDto = await lastValueFrom(request);

            if (!response) {
                throw null;
            }

            return Array.isArray(response) ? response : [response];
        } catch (e: any) {
            throw new Error(`No se ha encontrado ningun permiso. Error: ${e.message}`);
        }
    }

    async findAllPermisionsUser(userId: number): Promise<PermissionDto[]> {
        try {
            const request = this.httpClient.get<PermissionDto>(`${this.url}/findAllUser?userId=${userId}`);

            const response = await lastValueFrom(request);

            if (!response) {
                return [];
            }

            return Array.isArray(response) ? response : [response];
        } catch (e: any) {
            throw new Error(`No se ha encontrado ningun permiso. Error: ${e.message}`);
        }
    }


    async updatePermissionsApp(permissions: PermissionDto[]): Promise<any> {
        const request = this.httpClient.patch<PermissionDto>(`${this.url}/updateMany`, permissions);
        const response = await lastValueFrom(request);
        return response;
    }

    async updatePermissionsProfile(profileId: number, permissions: PermissionDto[]): Promise<any> {
        const request = this.httpClient.patch<PermissionDto>(`${this.url}/updateManyProfile`, { profileId, permissions });
        const response = await lastValueFrom(request);
        return response;
    }

    async updatePermissionsUser(userId: number, permissions: PermissionDto[]): Promise<any> {
        const request = this.httpClient.patch<PermissionDto>(`${this.url}/updateManyUser`, { userId, permissions });
        const response = await lastValueFrom(request);
        return response;
    }

    async findAvailableForUser(userId: number): Promise<PermissionDto[]> {
        try {
            const request = this.httpClient.get<PermissionDto[]>(`${this.url}/findAvailableForUser?userId=${userId}`);
            const response = await lastValueFrom(request);
            return Array.isArray(response) ? response : [];
        } catch (e: any) {
            throw new Error(`No se han podido obtener los permisos disponibles. Error: ${e.message}`);
        }
    }

    async addUserPermission(userId: number, permission: PermissionDto): Promise<PermissionDto> {
        const request = this.httpClient.post<PermissionDto>(`${this.url}/addUserPermission`, { userId, permission });
        const response = await lastValueFrom(request);
        return response;
    }

    async deleteUserPermission(userId: number, permissionId: number): Promise<any> {
        const request = this.httpClient.delete(`${this.url}/deleteUserPermission?userId=${userId}&permissionId=${permissionId}`);
        const response = await lastValueFrom(request);
        return response;
    }
}
